// =============================================================================
// Inventory Management — sites (demand) and one site in full.
//
// A site is a roof that needs modules. Deliberately NOT a project-management project: the portfolio
// tracks roughly three times as many sites as the project module has projects, and several sites share
// one property number, so sites stand on their own and carry a link to a project when one exists.
// =============================================================================

import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { Button } from '/imports/ui/Button';
import { Input } from '/imports/ui/Input';
import { Select } from '/imports/ui/Select';
import { toastOnError } from '/imports/ui/utils';
import { useMethod } from '/imports/hooks';
import {
  InventoryDeliveryStatus,
  inventoryAllocateSite, inventoryAllocationStatuses, inventoryDeliveryFlow, inventoryDeliveryStatusLabel,
  inventoryDeliveryStatuses, inventoryListSites, inventoryListWarehouses, inventoryRequestDelivery,
  inventorySetDeliveryStatus, inventorySiteDetail, inventorySiteStatusLabel, inventorySiteStatuses,
  inventoryUnassignPallets,
} from '/imports/api/inventory';
import {
  AllocationChip, DeliveryStatusChip, EmptyRow, Fact, InventoryPageStyled, PalletStatusChip,
  balance, count, day, money, orDash, stamp, useInventoryAccess,
} from '/imports/ui/InventoryShared';

const SitesPageStyled = styled(InventoryPageStyled)`
  .late { color: ${css.red5}; }
`;

/** Demand across every site: what each needs, what it holds, and where its delivery has got to. */
export const InventorySitesPage = () => {
  const { canAccess, canEdit } = useInventoryAccess();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [projectStatus, setProjectStatus] = useState('');
  const [allocation, setAllocation] = useState('');
  const [region, setRegion] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [deliveryStatus, setDeliveryStatus] = useState('');
  const [linked, setLinked] = useState('any');
  const [tick, setTick] = useState(0);

  const [warehouses] = useMethod(inventoryListWarehouses, undefined, []);
  const [data, loading] = useMethod(
    inventoryListSites,
    { search: search || undefined, projectStatus: (projectStatus || undefined) as any, allocation: (allocation || undefined) as any,
      region: region || undefined, closestWarehouseId: warehouseId || undefined,
      deliveryStatus: (deliveryStatus || undefined) as any, linked: linked as any, limit: 1000 },
    [search, projectStatus, allocation, region, warehouseId, deliveryStatus, linked, tick],
  );
  const rows = useMemo(() => data?.rows || [], [data]);

  const allocate = toastOnError(async (siteId: string) => {
    const result = await inventoryAllocateSite.call({ siteId });
    toast.success(strings.InventoryAssignedPallets(result.assignedPallets, siteId));
    if (result.shortfallModules) toast.info(strings.InventoryShortfall(result.shortfallModules));
    setTick((t) => t + 1);
  });

  if (!canAccess) return <NotPermittedPage />;

  return (
    <SitesPageStyled className="page">
      <PageHeading left={<h1>{strings.InventorySites()}</h1>} />
      <PageBody>
        <div className="inv-bar">
          <Input className="search" value={search} placeholder={strings.InventorySearchSitesPlaceholder()} onChange={(v) => setSearch(v || '')} />
          <Select
            value={projectStatus}
            options={[{ value: '', label: strings.InventoryAllSiteStatuses() }, ...inventorySiteStatuses.map((s) => ({ value: s, label: inventorySiteStatusLabel(s) }))]}
            onChange={setProjectStatus}
          />
          <Select
            value={allocation}
            options={[{ value: '', label: strings.InventoryAllAllocations() }, ...inventoryAllocationStatuses.map((s) => ({ value: s, label: s === 'complete' ? strings.Complete() : s === 'partial' ? strings.InventoryAllocationPartial() : strings.InventoryAllocationUnallocated() }))]}
            onChange={setAllocation}
          />
          <Select
            value={region}
            options={[{ value: '', label: strings.InventoryAllRegions() }, ...(data?.regions || []).map((r) => ({ value: r, label: r }))]}
            onChange={setRegion}
          />
          <Select
            value={warehouseId}
            options={[{ value: '', label: strings.InventoryAllWarehouses() }, ...(warehouses || []).map((w) => ({ value: w._id, label: `${w.code} — ${w.city}` }))]}
            onChange={setWarehouseId}
          />
          <Select
            value={deliveryStatus}
            options={[
              { value: '', label: strings.InventoryAllDeliveryStates() },
              { value: 'none', label: strings.InventoryNoDelivery() },
              ...inventoryDeliveryStatuses.filter((s) => s !== 'notRequested').map((s) => ({ value: s, label: inventoryDeliveryStatusLabel(s) })),
            ]}
            onChange={setDeliveryStatus}
          />
          <Select
            value={linked}
            options={[
              { value: 'any', label: strings.InventoryAllSites() },
              { value: 'linked', label: strings.InventoryLinkedOnly() },
              { value: 'unlinked', label: strings.InventoryUnlinkedOnly() },
            ]}
            onChange={setLinked}
          />
          <span className="spacer" />
          <span className="meta">{count(data?.count)}</span>
        </div>
        <div className="note">{strings.InventoryRequirementBasisNote()}</div>

        <div className="scroll-x">
          <table className="inv">
            <thead>
              <tr>
                <th>{strings.InventorySiteId()}</th>
                <th>{strings.Status()}</th>
                <th>{strings.InventoryTenant()}</th>
                <th>{strings.InventoryPropertyName()}</th>
                <th>{strings.State()}</th>
                <th>{strings.InventoryRegion()}</th>
                <th>{strings.InventoryClosestWarehouse()}</th>
                <th className="num">{strings.InventoryLntpSize()}</th>
                <th className="num">{strings.InventoryDesignSize()}</th>
                <th className="num">{strings.InventoryModulesRequired()}</th>
                <th className="num">{strings.InventoryPalletsRequired()}</th>
                <th className="num">{strings.InventoryModulesAllocated()}</th>
                <th className="num">{strings.InventoryPalletsAllocated()}</th>
                <th className="num">{strings.InventoryModuleBalance()}</th>
                <th>{strings.InventoryAllocationStatus()}</th>
                <th>{strings.InventoryDelivery()}</th>
                <th>{strings.InventoryMobilizationDate()}</th>
                <th>{strings.InventoryArrivedAt()}</th>
                <th>{strings.Project()}</th>
                {canEdit && <th />}
              </tr>
            </thead>
            <tbody>
              {loading && !rows.length && <tr><td colSpan={20}><LoadingFlex /></td></tr>}
              {rows.map((s) => (
                <tr key={s._id} className="clickable" onClick={() => navigate(`/inventory/sites/${s.siteId}`)}>
                  <td>{s.siteId}</td>
                  <td>{inventorySiteStatusLabel(s.projectStatus)}</td>
                  <td>{orDash(s.tenant)}</td>
                  <td>{s.propertyName}</td>
                  <td>{orDash(s.state)}</td>
                  <td>{orDash(s.region)}</td>
                  <td>{orDash(s.closestWarehouseCode)}</td>
                  <td className="num">{s.lntpSizeKw == null ? orDash(undefined) : s.lntpSizeKw.toFixed(2)}</td>
                  <td className="num">{s.finalDesignKw == null ? orDash(undefined) : s.finalDesignKw.toFixed(2)}</td>
                  <td className="num">{count(s.modulesRequired)}</td>
                  <td className="num">{count(s.palletsRequired)}</td>
                  <td className="num">{count(s.modulesAllocated)}</td>
                  <td className="num">{count(s.palletsAllocated)}</td>
                  <td className="num">{balance(s.moduleBalance)}</td>
                  <td><AllocationChip status={s.allocationStatus} /></td>
                  <td>{s.deliveryStatus ? <DeliveryStatusChip status={s.deliveryStatus} /> : orDash(undefined)}</td>
                  <td>{day(s.deliveryScheduledMobilizationDate)}</td>
                  <td className={(s.arrivalVsMobilizationDays || 0) > 0 ? 'late' : ''}>{day(s.deliveryArrivedAt)}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {s.projectManagementProjectId
                      ? <Link to={`/project-management/projects/${s.projectManagementProjectId}`}>{s.projectName || strings.Project()}</Link>
                      : <span className="muted">{strings.InventoryNotInProjectManagement()}</span>}
                  </td>
                  {canEdit && (
                    <td onClick={(e) => e.stopPropagation()}>
                      {s.allocationStatus !== 'complete' && s.modulesRequired != null
                        && <button className="link-btn" onClick={() => allocate(s.siteId)}>{strings.InventoryFillRequirement()}</button>}
                    </td>
                  )}
                </tr>
              ))}
              {!loading && !rows.length && <EmptyRow colSpan={20} label={strings.InventoryNoSites()} />}
            </tbody>
            {!!rows.length && data && (
              <tfoot>
                <tr>
                  <td colSpan={9}>{strings.Total()}</td>
                  <td className="num">{count(data.totals.modulesRequired)}</td>
                  <td className="num">{count(data.totals.palletsRequired)}</td>
                  <td className="num">{count(data.totals.modulesAllocated)}</td>
                  <td className="num">{count(data.totals.palletsAllocated)}</td>
                  <td className="num">{balance(data.totals.moduleBalance)}</td>
                  <td colSpan={canEdit ? 6 : 5} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </PageBody>
    </SitesPageStyled>
  );
};

/** One site: its sizing, the pallets held for it, its deliveries and what has been posted against it. */
export const InventorySitePage = () => {
  const { _id } = useParams<{ _id: string }>();
  const { canAccess, canEdit } = useInventoryAccess();
  const [tick, setTick] = useState(0);
  const [detail, loading] = useMethod(inventorySiteDetail, { siteId: _id as string }, [_id, tick]);
  const reload = () => setTick((t) => t + 1);

  const allocate = toastOnError(async () => {
    const result = await inventoryAllocateSite.call({ siteId: _id as string });
    toast.success(strings.InventoryAssignedPallets(result.assignedPallets, _id as string));
    if (result.shortfallModules) toast.info(strings.InventoryShortfall(result.shortfallModules));
    reload();
  });
  const request = toastOnError(async () => {
    await inventoryRequestDelivery.call({ siteId: _id as string });
    reload();
  });
  const advance = toastOnError(async (deliveryId: string, status: InventoryDeliveryStatus) => {
    const result = await inventorySetDeliveryStatus.call({ deliveryId, status });
    toast.success(strings.InventoryDeliveryAdvanced(inventoryDeliveryStatusLabel(status)));
    if (result.reductionsPosted) toast.info(strings.InventoryReductions());
    reload();
  });
  const unassign = toastOnError(async (palletId: string) => {
    await inventoryUnassignPallets.call({ palletIds: [palletId] });
    reload();
  });

  if (!canAccess) return <NotPermittedPage />;
  if (loading && !detail) return <SitesPageStyled className="page"><PageBody><LoadingFlex /></PageBody></SitesPageStyled>;
  if (!detail) return <SitesPageStyled className="page"><PageBody><div className="empty">{strings.NoResults()}</div></PageBody></SitesPageStyled>;

  const { site, row, pallets, deliveries, reductions, events } = detail;
  const openDelivery = deliveries.find((d) => d.status !== 'delivered' && d.status !== 'installed');
  const nextStatus = (current: InventoryDeliveryStatus): InventoryDeliveryStatus | undefined =>
    inventoryDeliveryFlow[inventoryDeliveryFlow.indexOf(current) + 1];

  return (
    <SitesPageStyled className="page">
      <PageHeading
        left={<h1>{site.siteId} — {site.propertyName}</h1>}
        right={canEdit ? (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {row.allocationStatus !== 'complete' && row.modulesRequired != null
              && <Button className="secondary compact" onClick={() => allocate()}>{strings.InventoryFillRequirement()}</Button>}
            {!openDelivery && !!pallets.length && <Button className="primary compact" onClick={() => request()}>{strings.InventoryRequestDelivery()}</Button>}
          </div>
        ) : undefined}
      />
      <PageBody>
        <div className="facts" style={{ marginBottom: '1rem' }}>
          <Fact label={strings.InventoryTenant()} value={site.tenant} />
          <Fact label={strings.Address()} value={site.address} />
          <Fact label={strings.Status()} value={inventorySiteStatusLabel(site.projectStatus)} />
          <Fact label={strings.InventoryGroup()} value={site.group} />
          <Fact label={strings.InventoryRegion()} value={site.region} />
          <Fact label={strings.InventoryClosestWarehouse()} value={row.closestWarehouseCode} />
          <Fact
            label={strings.Project()}
            value={site.projectManagementProjectId
              ? <Link to={`/project-management/projects/${site.projectManagementProjectId}`}>{detail.projectName || strings.Project()}</Link>
              : strings.InventoryNotInProjectManagement()}
          />
        </div>

        <div className="panels">
          <div className="panel-card">
            <h2>{strings.InventorySizing()}</h2>
            <div className="facts">
              <Fact label={strings.InventoryLntpSize()} value={site.lntpSizeKw == null ? undefined : `${site.lntpSizeKw.toFixed(2)} kW`} />
              <Fact label={strings.InventoryLntpAmount()} value={money(site.lntpAmount, true)} />
              <Fact label={strings.InventoryEquipmentWoSize()} value={site.noEquipmentWo ? strings.InventoryNoEquipmentWo() : site.equipmentWoSizeKw == null ? undefined : `${site.equipmentWoSizeKw.toFixed(2)} kW`} />
              <Fact label={strings.InventoryEquipmentWoAmount()} value={money(site.equipmentWoAmount, true)} />
              <Fact label={strings.InventoryFinalDesignSize()} value={site.finalDesignKw == null ? undefined : `${site.finalDesignKw.toFixed(2)} kW`} />
              <Fact label={strings.InventoryModulesRequired()} value={count(row.modulesRequired)} />
              <Fact label={strings.InventoryModulesForDesign()} value={count(row.modulesForDesign)} />
              <Fact label={strings.InventoryPalletsRequired()} value={count(row.palletsRequired)} />
              <Fact label={strings.InventoryModulesAllocated()} value={count(row.modulesAllocated)} />
              <Fact label={strings.InventoryModuleBalance()} value={balance(row.moduleBalance)} />
              <Fact label={strings.InventoryAllocationStatus()} value={<AllocationChip status={row.allocationStatus} />} />
              <Fact label={strings.InventoryTrucksRequired()} value={count(row.trucksRequired)} />
            </div>
            <div className="note">{strings.InventoryRequirementBasisNote()}</div>
          </div>

          <div className="panel-card">
            <h2>{strings.InventoryDeliveries()}</h2>
            <div className="scroll-x">
              <table className="inv">
                <thead>
                  <tr>
                    <th>{strings.Status()}</th>
                    <th className="num">{strings.InventoryPallet()}</th>
                    <th>{strings.InventoryRequestedAt()}</th>
                    <th>{strings.InventoryRequestedBy()}</th>
                    <th>{strings.InventoryMobilizationDate()}</th>
                    <th>{strings.InventoryShipDate()}</th>
                    <th>{strings.InventoryArrivedAt()}</th>
                    <th>{strings.InventoryArrivalVsMobilization()}</th>
                    {canEdit && <th />}
                  </tr>
                </thead>
                <tbody>
                  {deliveries.map((d) => {
                    const next = nextStatus(d.status);
                    return (
                      <tr key={d._id}>
                        <td><DeliveryStatusChip status={d.status} /></td>
                        <td className="num">{count(d.pallets)}</td>
                        <td>{day(d.requestedAt)}</td>
                        <td>{orDash(d.requestedByName)}</td>
                        <td>{day(d.scheduledMobilizationDate)}</td>
                        <td>{day(d.actualShipDate)}</td>
                        <td>{day(d.arrivedAt)}</td>
                        <td className={(d.arrivalVsMobilizationDays || 0) > 0 ? 'late' : ''}>
                          {d.arrivalVsMobilizationDays == null ? orDash(undefined)
                            : d.arrivalVsMobilizationDays > 0 ? strings.InventoryDaysLate(d.arrivalVsMobilizationDays)
                              : strings.InventoryDaysEarly(-d.arrivalVsMobilizationDays)}
                        </td>
                        {canEdit && (
                          <td>
                            {next && <button className="link-btn" onClick={() => advance(d._id, next)}>{inventoryDeliveryStatusLabel(next)}</button>}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                  {!deliveries.length && <EmptyRow colSpan={canEdit ? 9 : 8} label={strings.InventoryNoDeliveries()} />}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <h2 style={{ fontSize: '0.9rem', marginTop: '1.2rem' }}>{strings.InventoryPalletInventory()}</h2>
        <div className="scroll-x">
          <table className="inv">
            <thead>
              <tr>
                <th>{strings.InventoryPalletNumber()}</th>
                <th>{strings.InventoryPo()}</th>
                <th>{strings.InventoryWarehouse()}</th>
                <th>{strings.Status()}</th>
                <th className="num">{strings.InventoryModules()}</th>
                <th className="num">{strings.InventoryPalletValue()}</th>
                <th>{strings.InventoryAssignDate()}</th>
                {canEdit && <th />}
              </tr>
            </thead>
            <tbody>
              {pallets.map((p) => (
                <tr key={p._id}>
                  <td><Link to={`/inventory/pallet-inventory/${p._id}`}>{p.palletNumber}</Link></td>
                  <td>{orDash(p.poNumber)}</td>
                  <td>{orDash(p.warehouseCode)}</td>
                  <td><PalletStatusChip status={p.status} /></td>
                  <td className="num">{count(p.modules)}</td>
                  <td className="num">{p.unitCost == null ? orDash(undefined) : money((p.modules || 0) * p.unitCost)}</td>
                  <td>{day(p.assignedAt)}</td>
                  {canEdit && (
                    <td>{!p.deliveryId && <button className="link-btn" onClick={() => unassign(p._id)}>{strings.InventoryUnassign()}</button>}</td>
                  )}
                </tr>
              ))}
              {!pallets.length && <EmptyRow colSpan={canEdit ? 8 : 7} label={strings.InventoryNoPallets()} />}
            </tbody>
          </table>
        </div>

        {!!reductions.length && <>
          <h2 style={{ fontSize: '0.9rem', marginTop: '1.2rem' }}>{strings.InventoryReductions()}</h2>
          <div className="scroll-x">
            <table className="inv">
              <thead>
                <tr>
                  <th>{strings.InventoryCountColumn()}</th>
                  <th>{strings.Date()}</th>
                  <th className="num">{strings.Quantity()}</th>
                  <th className="num">{strings.InventoryLandedUnitPrice()}</th>
                  <th className="num">{strings.InventoryReductionAmount()}</th>
                  <th className="num">{strings.InventorySalesTaxPaid()}</th>
                  <th>{strings.InventoryUseTaxDue()}</th>
                </tr>
              </thead>
              <tbody>
                {reductions.map((r) => (
                  <tr key={r._id}>
                    <td>{r.count}</td>
                    <td>{day(r.date)}</td>
                    <td className="num">{count(r.quantity)}</td>
                    <td className="num">{money(r.price, true)}</td>
                    <td className="num">{money(r.amount, true)}</td>
                    <td className="num">{money(r.salesTaxPaid, true)}</td>
                    <td>{r.useTaxDue ? strings.Yes() : strings.No()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>}

        {!!events.length && <>
          <h2 style={{ fontSize: '0.9rem', marginTop: '1.2rem' }}>{strings.InventoryViewEvents()}</h2>
          <table className="inv">
            <tbody>
              {events.map((e) => (
                <tr key={e._id}>
                  <td>{e.label || e.kind}</td>
                  <td className="muted">{e.byName}</td>
                  <td className="muted">{stamp(e.at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>}
      </PageBody>
    </SitesPageStyled>
  );
};
